import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchPosts, fetchTagsBySearch } from "../api/posts";
import PostGrid from "../components/PostGrid";

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function getQueryFromSearch(search) {
  const params = new URLSearchParams(search);
  return params.get("q")?.trim() || "";
}

function postHasMatchingTag(post, normalizedQuery) {
  const termGroups = post?._embedded?.["wp:term"];
  if (!Array.isArray(termGroups)) return false;

  const tagTerms = termGroups
    .flat()
    .filter((term) => term?.taxonomy === "post_tag");

  const cleanedQuery = String(normalizedQuery || "").replace(/0/g, "o");
  if (!cleanedQuery) return false;

  // Match on any progressive prefix: "s", "so", "sol" ...
  const prefixes = Array.from({ length: cleanedQuery.length }, (_, i) => cleanedQuery.slice(0, i + 1));

  return tagTerms.some((term) => {
    const tagName = String(term?.name || "").toLowerCase();
    const cleanedTag = tagName.replace(/0/g, "o");
    return prefixes.some((prefix) => cleanedTag.includes(prefix));
  });
}

export default function SearchResults({ darkMode }) {
  const location = useLocation();
  const query = useMemo(() => getQueryFromSearch(location.search), [location.search]);
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const normalizedQuery = query.toLowerCase();

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (query.length < 2) {
        setPosts([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Keep WP side broad, then strictly filter by tag match.
        const tags = await fetchTagsBySearch(query, { per_page: 50 });
        const tagIds = tags.map((t) => t?.id).filter(Boolean);
        if (!tagIds.length) {
          setPosts([]);
          return;
        }

        const wpPosts = await fetchPosts({
          tags: tagIds.join(","),
          per_page: 48,
          orderby: "date",
          order: "desc",
        });

        if (cancelled) return;
        const safePosts = Array.isArray(wpPosts) ? wpPosts : [];
        const filtered = safePosts.filter((post) => postHasMatchingTag(post, normalizedQuery));
        setPosts(filtered);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [query, normalizedQuery]);

  return (
    <section className="w-full">
      <div className="mx-auto w-full max-w-[1500px] px-4 py-10">
        <h1
          className={`text-[26px] md:text-[32px] font-normal ${
            darkMode ? "text-white" : "text-black"
          }`}
          style={{ fontFamily: "Poppins, sans-serif" }}
        >
          Search results for &quot;{stripHtml(query)}&quot;
        </h1>

        <PostGrid
          posts={posts}
          loading={loading}
          categoryLabel="SEARCH"
          categorySlug="search"
          categoryLink="#"
          showCategoryPills={false}
          showCategoryTag={false}
          mixedCategories={true}
          darkMode={darkMode}
        />
      </div>
    </section>
  );
}
